import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader.js';
import { useTenantContext } from '../api/tenant-context.js';
import { getPlatformStatus, type IntegrationStatus } from '../api/platform-status.js';

const STATUS_TONE: Record<IntegrationStatus['status'], string> = {
  ok: 'dot-ok',
  warn: 'dot-warn',
  err: 'dot-err',
  idle: 'dot-idle',
};

const STATUS_TEXT: Record<IntegrationStatus['status'], string> = {
  ok: 'Operativo',
  warn: 'Advertencia',
  err: 'Error',
  idle: 'Sin configurar',
};

export function Estado(): JSX.Element {
  const { tenant } = useTenantContext();
  const tenantId = tenant?.id ?? '';
  const q = useQuery({
    queryKey: ['platform-status', tenantId],
    queryFn: () => getPlatformStatus(tenantId),
    enabled: !!tenantId,
    refetchInterval: 30_000,
  });

  return (
    <>
      <PageHeader
        title="Estado de plataforma"
        subtitle="Salud de las integraciones del workspace y servicios backend."
      />
      {q.isLoading && <div className="panel text-ink-3 text-sm">Cargando…</div>}
      {q.error && <div className="panel text-err text-sm">{(q.error as Error).message}</div>}
      {q.data && (
        <div className="space-y-5">
          <OverallBanner status={q.data.overall} generatedAt={q.data.generatedAt} />
          <Section title="Integraciones externas" items={q.data.integrations} />
          <Section title="Backend AWS" items={q.data.backend} />
        </div>
      )}
    </>
  );
}

function OverallBanner({
  status,
  generatedAt,
}: {
  status: 'ok' | 'warn' | 'err';
  generatedAt: string;
}): JSX.Element {
  const label =
    status === 'ok'
      ? 'Todo está operativo'
      : status === 'warn'
        ? 'Algunas integraciones requieren atención'
        : 'Hay errores que necesitás resolver';
  const tone =
    status === 'ok'
      ? 'border-emerald-700 bg-emerald-900/20 text-emerald-300'
      : status === 'warn'
        ? 'border-amber-700 bg-amber-900/20 text-amber-300'
        : 'border-red-700 bg-red-900/20 text-red-300';
  return (
    <div className={`rounded-lg border p-4 flex items-center justify-between ${tone}`}>
      <div>
        <div className="text-[15px] font-semibold">{label}</div>
        <div className="text-[12px] opacity-80 mt-0.5">
          Última verificación: {new Date(generatedAt).toLocaleString()}
        </div>
      </div>
      <span className={`dot ${STATUS_TONE[status]}`} style={{ width: 12, height: 12 }} />
    </div>
  );
}

function Section({ title, items }: { title: string; items: IntegrationStatus[] }): JSX.Element {
  return (
    <section className="panel">
      <div className="text-[14px] font-semibold mb-3">{title}</div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 rounded border border-border p-3">
            <span className={`dot ${STATUS_TONE[it.status]}`} />
            <div className="flex-1">
              <div className="text-[13px] font-medium">{it.label}</div>
              {it.meta && <div className="text-[11.5px] text-ink-4 mono mt-0.5">{it.meta}</div>}
            </div>
            <span
              className={`badge ${
                it.status === 'ok'
                  ? 'badge-ok'
                  : it.status === 'warn'
                    ? 'badge-warn'
                    : it.status === 'err'
                      ? 'badge-err'
                      : 'badge-neutral'
              }`}
            >
              {STATUS_TEXT[it.status]}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
